import {BindPath} from './BindPath';

export interface IValidationResult
{
    isValid:boolean;
    errorMessages:ReadonlyArray<string>;
}

abstract class ValidationResultBase implements IValidationResult
{
    public readonly isValid:boolean;
    public readonly errorMessages:ReadonlyArray<string>;

    protected constructor(isValid:boolean, errorMessages?:ReadonlyArray<string>)
    {
        this.isValid = isValid;
        this.errorMessages = errorMessages || [];
    }
}

export class ValidationResult extends ValidationResultBase
{
    private constructor(isValid:boolean, errorMessages?:ReadonlyArray<string>)
    {
        super(isValid, errorMessages);
    }

    static valid():ValidationResult
    {
        return new ValidationResult(true);
    }

    static error(...errorMessages:Array<string>):ValidationResult
    {
        return new ValidationResult(false, errorMessages);
    }
}

export class FieldValidationResult extends ValidationResultBase
{
    public readonly bindPath:BindPath;

    private constructor(bindPath:BindPath, isValid:boolean, errorMessages?:ReadonlyArray<string>)
    {
        super(isValid, errorMessages);

        this.bindPath = bindPath;
    }

    static valid(bindPath:BindPath):FieldValidationResult
    {
        return new FieldValidationResult(bindPath, true);
    }

    static error(bindPath:BindPath, ...errorMessages:Array<string>):FieldValidationResult
    {
        return new FieldValidationResult(bindPath, false, errorMessages);
    }
}

export class ObjectValidationResult implements IValidationResult
{
    public readonly fields:ReadonlyArray<FieldValidationResult>;

    public constructor(props?:ReadonlyArray<FieldValidationResult>)
    {
        this.fields = props || [];
    }

    public get isValid():boolean
    {
        return this.fields.filter(field => !field.isValid).length == 0;
    }

    public get errorMessages():ReadonlyArray<string>
    {
        const result = [];

        this.fields
            .forEach(field => field.errorMessages
                .forEach(message => result.push(message)));

        return result;
    }
}

export class ValidationResultBuilder
{
    fields:Map<string, FieldValidationResult>;

    public constructor()
    {
        this.fields = new Map<string, FieldValidationResult>();
    }

    valid(bindPath:BindPath):ValidationResultBuilder
    {
        this.fields.set(this.getKey(bindPath), FieldValidationResult.valid(bindPath));
        return this;
    }

    error(bindPath:BindPath, ...errorMessages:Array<string>):ValidationResultBuilder
    {
        const key = this.getKey(bindPath);

        const fieldResult = this.fields.get(key);
        if( fieldResult && !fieldResult.isValid )
        {
            this.fields.set(key, FieldValidationResult.error(bindPath, ...[...fieldResult.errorMessages, ...errorMessages]));
        }
        else
        {
            this.fields.set(key, FieldValidationResult.error(bindPath, ...errorMessages));
        }

        return this;
    }

    result():ObjectValidationResult
    {
        return new ObjectValidationResult(Array.from(this.fields.values()));
    }

    private getKey(bindPath:BindPath)
    {
        return `${bindPath.bindGroupName}_${bindPath.fieldPath}`;
    }
}
